class GiftCampaignGoal extends HTMLElement {
  constructor() {
    super();

    this.selectors = {
      leftToSpend: '[data-left-to-spend]',
      picker: '[data-gift-picker]',
      giftInput: '.gift-campaign-option__input',
      giftSummary: '[data-gift-summary]',
      giftOptionsWrapper: '[data-gift-options-wrapper]',
      changeGift: '[data-change-gift]',
      selectedGiftTitle: '[data-selected-gift-title]',
    };
    // data-minimum-amount is already the correct number for the shopper's active
    // currency (Liquid picks the matching per-market field) — no conversion needed.
    this.goal = Number(this.dataset.minimumAmount) || 0;
    this.progress = this.querySelector('progress-bar');
    this.money_format = window.FoxTheme.settings.moneyFormat;
    this.campaignProductIds = (this.dataset.campaignProductIds || '').split(',').filter(Boolean);
    this.giftVariantIds = (this.dataset.giftVariantIds || '').split(',').filter(Boolean);
    this.selectedGiftVariantId = this.dataset.selectedGiftVariantId || '';
    // Once a gift is already selected, default to the compact "Free gift: X" summary
    // instead of the full picker, so it doesn't eat the drawer's visible space.
    this.showOptions = !this.selectedGiftVariantId;
  }

  connectedCallback() {
    this.bindGiftInputs();
    this.update(Number(this.dataset.cartTotal), this.selectedGiftVariantId);
    document.addEventListener('cart:updated', (event) => this.onCartUpdated(event.detail.cart));
  }

  onCartUpdated(cart) {
    const campaignSubtotal = cart.items
      .filter((item) => this.campaignProductIds.includes(String(item.product_id)))
      .reduce((sum, item) => sum + item.line_price, 0);

    const selectedItem = cart.items.find((item) => this.giftVariantIds.includes(String(item.variant_id)));
    this.selectedGiftVariantId = selectedItem ? String(selectedItem.variant_id) : '';

    this.update(campaignSubtotal, this.selectedGiftVariantId);

    if (this.goalLeft > 0 && selectedItem) {
      // Cart dropped back below the goal after a gift was already added — drop it.
      this.removeGift(selectedItem.variant_id);
    }
  }

  update(amount, selectedGiftVariantId) {
    if (!this.goal) return;

    this.cartTotal = amount / 100;
    this.goalLeft = this.goal - this.cartTotal;
    const unlocked = this.goalLeft <= 0;

    this.percent = Math.min((this.cartTotal * 100) / this.goal, 100);
    if (this.progress) {
      this.progress.style.setProperty('--percent', `${this.percent}%`);
      this.progress.dataset.value = this.cartTotal;
      this.progress.dataset.max = this.goal;
    }

    this.classList.toggle('gift-campaign-goal--done', unlocked);

    const spend = Math.max(this.goal - this.cartTotal, 0) * 100;
    const leftToSpendEl = this.querySelector(this.selectors.leftToSpend);
    if (leftToSpendEl) {
      leftToSpendEl.innerHTML = FoxTheme.Currency.formatMoney(spend, this.money_format);
    }

    const picker = this.querySelector(this.selectors.picker);
    if (picker) picker.hidden = !unlocked;

    this.querySelectorAll(this.selectors.giftInput).forEach((input) => {
      input.checked = input.value === selectedGiftVariantId;
    });

    this.toggleGiftView(selectedGiftVariantId);
  }

  toggleGiftView(selectedGiftVariantId) {
    const summary = this.querySelector(this.selectors.giftSummary);
    const optionsWrapper = this.querySelector(this.selectors.giftOptionsWrapper);
    const showSummary = Boolean(selectedGiftVariantId) && !this.showOptions;

    if (summary) summary.hidden = !showSummary;
    if (optionsWrapper) optionsWrapper.hidden = showSummary;

    if (showSummary) {
      const checkedInput = this.querySelector(`${this.selectors.giftInput}[value="${selectedGiftVariantId}"]`);
      const title = checkedInput?.closest('label')?.querySelector('.gift-campaign-option__title')?.textContent || '';
      const titleEl = this.querySelector(this.selectors.selectedGiftTitle);
      if (titleEl) titleEl.textContent = title;
    }
  }

  bindGiftInputs() {
    this.querySelectorAll(this.selectors.giftInput).forEach((input) => {
      input.addEventListener('change', (event) => {
        if (event.target.checked) this.selectGift(event.target.value);
      });
    });

    const changeBtn = this.querySelector(this.selectors.changeGift);
    if (changeBtn) {
      changeBtn.addEventListener('click', () => {
        this.showOptions = true;
        this.toggleGiftView(this.selectedGiftVariantId);
      });
    }
  }

  selectGift(variantId) {
    const updates = {};
    if (this.selectedGiftVariantId && this.selectedGiftVariantId !== variantId) {
      updates[this.selectedGiftVariantId] = 0;
    }
    updates[variantId] = 1;
    // Collapse back to the compact summary once a choice is made.
    this.showOptions = false;
    this.updateCart(updates);
  }

  removeGift(variantId) {
    this.updateCart({ [variantId]: 0 });
  }

  updateCart(updates) {
    const sectionsToUpdate = this.getSectionsToUpdate();
    const body = JSON.stringify({ updates, sections: sectionsToUpdate });

    fetch(`${FoxTheme.routes.cart_update_url}`, { ...FoxTheme.utils.fetchConfig(), ...{ body } })
      .then((response) => response.json())
      .then((parsedState) => {
        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: parsedState });
      })
      .catch((error) => {
        console.error('Error updating gift campaign selection:', error);
      });
  }

  getSectionsToUpdate() {
    let sections = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections } })
    );
    return sections;
  }
}
customElements.define('gift-campaign-goal', GiftCampaignGoal);
